import React, { useState, useEffect } from 'react';
import PageHeading from '../PageHeading';

const EmailConfig = () => {
  // State for form inputs
  const [formData, setFormData] = useState({
    smtpHost: '',
    smtpPort: '',
    emailUser: '',
    emailPassword: '',
    fromName: '',
  });

  // State for current configuration and UI control
  const [currentConfig, setCurrentConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConfigAdded, setIsConfigAdded] = useState(false);

  // API key (ideally from environment variable)
  const API_KEY = process.env.API_KEY || 'caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079'; // Replace with your actual API key

  // Fetch current configuration on component mount
  useEffect(() => {
    fetchCurrentConfig();
  }, []);

  // Function to fetch current configuration
  const fetchCurrentConfig = async () => {
    try {
      const response = await fetch('https://api.muktihospital.com/api/email-config/current', {
        headers: {
          'x-api-key': API_KEY,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch current configuration');
      }
      const config = await response.json();
      setCurrentConfig(config);
      setIsConfigAdded(true); // If config exists, mark as added
      // Populate form with current config
      setFormData({
        smtpHost: config.smtpHost || '',
        smtpPort: config.smtpPort || '',
        emailUser: config.emailUser || '',
        emailPassword: '', // Don't prefill password for security
        fromName: config.fromName || '',
      });
    } catch (err) {
      setError(err.message);
      setIsConfigAdded(false); // No config exists, allow adding
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle adding new configuration
  const handleAddConfig = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://api.muktihospital.com/api/email-config/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to add email configuration');
      }

      const result = await response.json();
      // console.log(result);
      await fetchCurrentConfig(); // Refresh current config
      alert('Email configuration added successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle updating configuration
  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const updatedData = { ...formData };

    // Only include password if it is provided (not empty)
    if (!updatedData.emailPassword) {
      delete updatedData.emailPassword; // Don't send the password if it's empty
    }

    // console.log("Form Data being sent:", updatedData);  // Log the data being sent

    try {
      const response = await fetch('https://api.muktihospital.com/api/email-config/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
        },
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        throw new Error('Failed to update email configuration');
      }

      const result = await response.json();
      // console.log(result);
      await fetchCurrentConfig(); // Refresh current config
      alert('Email configuration updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Email Configuration" },
    { label: "Email Config" },
  ];
  return (
     <div>
      <div className="print-hidden">
        <PageHeading title="Email Config" breadcrumbs={breadcrumbs} />
      </div>
         <div className=" bg-white rounded-lg shadow-md">
         <div className="flex flex-wrap gap-3 flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
        <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">Update Email Configuration</h2>
        </div>
        {/* Form for adding/updating configuration */}
        <form onSubmit={isConfigAdded ? handleUpdateConfig : handleAddConfig} className="space-y-4 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">SMTP Host:</label>
              <input
                type="text"
                name="smtpHost"
                value={formData.smtpHost}
                onChange={handleInputChange}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">SMTP Port:</label>
              <input
                type="number"
                name="smtpPort"
                value={formData.smtpPort}
                onChange={handleInputChange}
                className="input-field"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Email User:</label>
              <input
                type="email"
                name="emailUser"
                value={formData.emailUser}
                onChange={handleInputChange}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Email Password:</label>
              <input
                type="password"
                name="emailPassword"
                value={formData.emailPassword}
                onChange={handleInputChange}
                placeholder="••••••••"
                className="input-field"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">From Name:</label>
            <input
              type="text"
              name="fromName"
              value={formData.fromName}
              onChange={handleInputChange}
              className="input-field"
              required
            />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
          >
            {loading ? 'Processing...' : isConfigAdded ? 'Update Configuration' : 'Add Configuration'}
          </button>
        </form>

        {/* Current Configuration Section */}
        <div className="p-4">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Current Configuration</h3>
          {currentConfig ? (
            <div className="border border-gray-200 rounded-md">
              <table className="w-full text-sm text-gray-700">
                <tbody>
                  <tr className="border-b flex flex-col">
                    <td className="p-3 bg-gray-100 font-semibold">SMTP Host</td>
                    <td className="p-3">{currentConfig.smtpHost}</td>
                  </tr>
                  <tr className="border-b flex flex-col">
                    <td className="p-3 font-semibold bg-gray-100">SMTP Port</td>
                    <td className="p-3">{currentConfig.smtpPort}</td>
                  </tr>
                  <tr className="border-b flex flex-col">
                    <td className="p-3 font-semibold bg-gray-100">Email User</td>
                    <td className="p-3">{currentConfig.emailUser}</td>
                  </tr>
                  <tr className='flex flex-col'>
                    <td className="p-3 font-semibold bg-gray-100">From Name</td>
                    <td className="p-3">{currentConfig.fromName}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No configuration found. Please add one.</p>
          )}
        </div>
      </div>
     </div>
  );
};

export default EmailConfig;
